import { call, put, select, takeEvery } from "redux-saga/effects"
import api from "../../utilities/api"
import * as apiErrorHelpers from "../../helpers/apiErrorHelpers"
import {
  SAVE_TIRE_DETAILS,
  SAVE_TIRE_SIZE_DETAILS,
  SAVE_SELECTED_SHOP,
  SAVE_INSTALLATION_DATE,
  SAVE_SELECTED_TIRE,
  RESET_TIRE_DATA,
  USER_NOT_LOADING
} from "../tire_action/tiresActions"
import { TIRE_CONNECT } from "../../constants/tires"
import * as sessionHelpers from "../../helpers/sessionHelpers"
import { AUTHENTICATION_TOKEN } from "../../constants/application"
import { constants } from "caradvise_shared_components"
const UPDATE_VEHICLE_VIN = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/vehicles`
const TIRES_ORDERS_URL = "/orders/tire_orders"
// Helper function to get user state
function* getUserState() {
  return yield select((state) => state.user || {})
}

function toQueryString(params) {
  return Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`).join("&")
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      }
    })
    .join("&")
}

function* apiCallSaga(actionType, method, url, body, callback) {
  try {
    const authentication_token = yield select(
      (state) => state?.application?.userSessionData?.authentication_token
    )

    if (!authentication_token) {
      throw new Error("Authentication token missing")
    }

    const upperMethod = method.toUpperCase()

    const headers = {
      Authorization: authentication_token,
      "Content-Type": "application/json"
    }

    const fetchOptions = {
      method: upperMethod,
      headers
    }

    if (body) {
      if (["GET", "HEAD"].includes(upperMethod)) {
        const queryString = toQueryString(body)
        if (queryString) {
          url += (url.includes("?") ? "&" : "?") + queryString
        }
      } else {
        fetchOptions.body = JSON.stringify(body)
      }
    }

    const response = yield call(fetch, url, fetchOptions)
    const responseBody = yield call([response, "json"])

    apiErrorHelpers.catchErrors(responseBody)
    if (callback) {
      const result = callback("success", responseBody)
      // If callback is a generator, it will return an iterator
      if (result && typeof result.next === "function" && typeof result.throw === "function") {
        yield* result
      }
    }
    return responseBody
  } catch (error) {
    const errors = apiErrorHelpers.formatInternalErrors(error)
    if (callback) callback("failure", { error: apiErrorHelpers.toString(errors) })
    yield put({ type: `${actionType}_FAILED`, payload: errors })
    apiErrorHelpers.consoleLog(errors)
  } finally {
    yield put({ type: USER_NOT_LOADING })
  }
}

// Worker Sagas
function* saveTireDetailsSaga({ payload }) {
  yield put({ type: SAVE_TIRE_DETAILS, payload })
}

function* saveTireSizeDetailsSaga({ payload }) {
  yield put({ type: SAVE_TIRE_SIZE_DETAILS, payload })
}

function* saveSelectedTireSaga({ payload }) {
  yield put({ type: SAVE_SELECTED_TIRE, payload })
}

function* saveSelectedShopSaga({ payload }) {
  yield put({ type: SAVE_SELECTED_SHOP, payload })
}

function* saveInstallationDateSaga({ payload }) {
  yield put({ type: SAVE_INSTALLATION_DATE, payload })
}

function* resetTireDataSaga() {
  yield put({ type: RESET_TIRE_DATA })
}

function* getFilteredListOfTiresSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/tires`
  yield* apiCallSaga("GET_FILTERED_TIRES", "get", url, payload, callback)
}

function* getTireDetailsSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/tires/${payload}`
  yield* apiCallSaga("GET_TIRE_DETAILS", "get", url, {}, callback)
}

function* getRimSizeSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v2/tires/sizes`
  const body = { vehicle_id: payload.vehicleId }

  yield* apiCallSaga("GET_RIM_SIZE_SAGA", "get", url, body, callback)
}

function* getShopsForTireSaga({ payload, callback }) {
  const vehicleID = yield select((state) => state?.tires?.vehicle?.id)
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/v1/shops`
  const body = {
    vehicle_id: vehicleID,
    tire_integration: TIRE_CONNECT,
    ...payload
  }
  yield* apiCallSaga("GET_SHOPS_FOR_TIRE_SAGA", "get", url, body, callback)
}

function* getTireBookingDetailsSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/tires/preview`
  const body = {
    tires: payload.tires,
    shop_id: payload.shop_id,
    supplier: payload.supplier
  }

  yield* apiCallSaga("GET_TIRE_BOOKING_DETAILS", "post", url, body, callback)
}

function* getTimeSlotsForShopSaga({ payload }) {
  const user = yield* getUserState()
  const { authentication_token } = user

  if (authentication_token) {
    const url = `/api/v4/shops/${payload.shopId}/schedule?date=${payload.date}&days=1&service_definition_ids[]=[]`
    yield* apiCallSaga("GET_TIME_SLOTS_FOR_SHOP", "get", url, {}, payload.callback)
  }
}

function* getTireOrderDetailsSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/orders/${payload}`

  yield* apiCallSaga("GET_TIRE_ORDER_DETAILS", "get", url, {}, callback)
}

function* updateVehicleDetailsSaga({ payload }) {
  const { user, data, vehicleId } = payload
  const { authentication_token } = user

  if (authentication_token) {
    const url = `${UPDATE_VEHICLE_VIN}/${vehicleId}`

    yield* apiCallSaga("UPDATE_VEHICLE_DETAILS", "patch", url, data)
  }
}

export function* fetchTimeSlotsFromServer(action) {
  try {
    const { shopId, date } = action.payload
    const url = `/shops/${shopId}/schedule?date=${date}&days=1`
    const authentication_token = yield select(
      (state) => state.application.userSessionData.authentication_token
    )
    api.setAuthApi({
      Authorization: authentication_token
    })
    const response = yield call(api.utility.get, api.path(url))
    apiErrorHelpers.catchErrors(response.body)
    if (action.payload.callback) {
      yield call(action.payload.callback, response.body)
    }
    return response.body
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    apiErrorHelpers.consoleLog(errors)
    if (action.payload.callback) {
      yield call(action.payload.callback, { error: apiErrorHelpers.toString(errors) })
    }
    return { error: apiErrorHelpers.toString(errors) }
  }
}

function* fetchVehicleByIdSaga(action) {
  try {
    const authentication_token = yield select(
      (state) => state.application.userSessionData.authentication_token
    )

    if (!authentication_token) {
      throw new Error("Authentication token missing")
    }

    const { vehicleId, callback } = action.payload
    api.setAuthApi({
      Authorization: authentication_token
    })
    const response = yield call(api.utility.get, api.path(`/vehicles/${vehicleId}`))
    apiErrorHelpers.catchErrors(response.body)
    if (callback) {
      yield call(callback, response.body)
    }
    return response.body
  } catch (errors) {
    errors = apiErrorHelpers.formatInternalErrors(errors)
    apiErrorHelpers.consoleLog(errors)
    if (action.payload && action.payload.callback) {
      yield call(action.payload.callback, { error: apiErrorHelpers.toString(errors) })
    }
    return { error: apiErrorHelpers.toString(errors) }
  }
}

function* bookTiresSaga({ payload, callback }) {
  const url = `${process.env.REACT_APP_INTERNAL_API_URL}/api/ecosystem/v1/tires/tire_connect_orders`
  yield* apiCallSaga("GET_FILTERED_TIRES", "post", url, payload, callback)
}

export function* fetchTireOrderListSaga({ payload }) {
  function* callback(status, data) {
    yield put({
      type: "TIRE_ORDERS_LOAD_SUCCEEDED",
      payload: { tireOrders: data.orders }
    })
  }
  const url = `/api/v6/orders/tire_orders`
  yield* apiCallSaga("GET_TIRE_ORDERS", "get", url, payload, callback)
}

function* sendSmsSaga({ payload, callback }) {
  const url = `/api/v6/users/send_tire_installation_sms`
  const body = {
    user_id: payload.user_id,
    phone_number: payload.phone_number,
    order_id: payload.order_id
  }
  yield* apiCallSaga("SEND_INSTRUCTIONS_SMS", "post", url, body, callback)
}

export function* downloadTiresShopOrders(action) {
  const payload = action.payload || {}
  const { startDate, endDate } = payload
  const authToken = sessionHelpers.getApplicationCookie(AUTHENTICATION_TOKEN)
  const { STATUS, PAID } = constants.orders

  const PAST_ORDER_SCHEDULED_STATUSES_FROM_RETAIL = [PAID].map((string) => STATUS[string])
  const url = `${TIRES_ORDERS_URL}.csv?auth_token=${authToken}&completed=true&appointment_datetime_from=${startDate}&appointment_datetime_to=${endDate}&state_id=${PAST_ORDER_SCHEDULED_STATUSES_FROM_RETAIL}`
  window.open(api.pathV6(url))
}

export default function* tiresSaga() {
  yield takeEvery("GET_FILTERED_LIST_OF_TIRES_SAGA", getFilteredListOfTiresSaga)
  yield takeEvery("GET_RIM_SIZE_SAGA", getRimSizeSaga)
  yield takeEvery("GET_SHOPS_FOR_TIRE_SAGA", getShopsForTireSaga)
  yield takeEvery("TIRE_FETCH_TIMESLOTS_SAGA", fetchTimeSlotsFromServer)
  yield takeEvery("TIRE_FETCH_VEHICLE_BY_ID_SAGA", fetchVehicleByIdSaga)
  yield takeEvery("TIRE_BOOKING_PREVIEW_SAGA", getTireBookingDetailsSaga)
  yield takeEvery("BOOK_TIRE_ORDER_SAGA", bookTiresSaga)
  yield takeEvery("TIRE_ORDER_DETAILS_SAGA", getTireOrderDetailsSaga)
  yield takeEvery("SEND_INSTRUCTIONS_SMS", sendSmsSaga)
  yield takeEvery("GET_TIRES_DETAILS_BY_ID_SAGA", getTireDetailsSaga)
  yield takeEvery("GET_TIRE_ORDERS_LIST_SAGA", fetchTireOrderListSaga)
  yield takeEvery("TIRES_SHOP_ORDERS_DOWNLOAD_SAGA", downloadTiresShopOrders)
}
